import type { Substance, SubstanceClass } from '../pharmacology/types';
import { SUBSTANCE_CLASS_LABELS } from '../pharmacology/types';
import { findSubstanceMatch } from '../pharmacology/fuzzyMatch';
import { substancesByClass } from '../pharmacology/substances/index';
import { requireElement } from './domHelpers';

/**
 * Search field plus a class-grouped browser.
 *
 * With this many substances a flat chip row stops being browsable, so entries
 * are grouped by pharmacological class. Typing filters across every group at
 * once and keeps only the groups that still have matches, so search and browse
 * are the same surface rather than two competing ones.
 */
export class SubstanceBrowser {
  private readonly inputEl: HTMLInputElement;
  private readonly clearBtn: HTMLButtonElement;
  private readonly groupsEl: HTMLElement;
  private readonly toggleBtn: HTMLButtonElement;
  private readonly panelEl: HTMLElement;
  private readonly buttons = new Map<string, HTMLButtonElement>();

  private selectedId: string | null = null;

  constructor(
    private readonly substances: Substance[],
    private readonly onChange: (substance: Substance | null) => void,
  ) {
    this.inputEl = requireElement<HTMLInputElement>('substance-input');
    this.clearBtn = requireElement<HTMLButtonElement>('clear-btn');
    this.groupsEl = requireElement<HTMLElement>('substance-groups');
    this.toggleBtn = requireElement<HTMLButtonElement>('browser-toggle');
    this.panelEl = requireElement<HTMLElement>('browser-panel');

    this.renderGroups();

    // On a phone the browser would cover most of the scene, so it starts
    // collapsed there and opens on demand (or as soon as the user searches).
    if (window.matchMedia('(max-width: 640px)').matches) {
      this.setPanelOpen(false);
    }

    this.inputEl.addEventListener('input', () => this.handleInput());
    this.inputEl.addEventListener('focus', () => this.setPanelOpen(true));
    this.inputEl.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.clear();
    });
    this.clearBtn.addEventListener('click', () => this.clear());
    this.toggleBtn.addEventListener('click', () => this.setPanelOpen(!this.isPanelOpen()));
  }

  select(substance: Substance): void {
    this.selectedId = substance.id;
    this.inputEl.value = substance.displayName;
    this.clearBtn.classList.add('visible');
    this.filter('');
    this.markSelection();
    this.onChange(substance);
  }

  private clear(): void {
    this.selectedId = null;
    this.inputEl.value = '';
    this.clearBtn.classList.remove('visible');
    this.filter('');
    this.markSelection();
    this.onChange(null);
  }

  private handleInput(): void {
    const query = this.inputEl.value.trim();
    this.filter(query);

    if (!query) {
      this.clearBtn.classList.remove('visible');
      this.selectedId = null;
      this.markSelection();
      this.onChange(null);
      return;
    }

    this.clearBtn.classList.add('visible');
    this.setPanelOpen(true);

    const match = findSubstanceMatch(query, this.substances);
    this.selectedId = match?.id ?? null;
    this.markSelection();
    this.onChange(match);
  }

  /** Hides non-matching entries, and any group left with nothing to show. */
  private filter(query: string): void {
    const needle = query.trim().toLowerCase();

    for (const group of Array.from(this.groupsEl.children) as HTMLElement[]) {
      let visibleCount = 0;

      for (const button of Array.from(group.querySelectorAll('button')) as HTMLButtonElement[]) {
        const haystack = button.dataset.search ?? '';
        const visible = !needle || haystack.includes(needle);
        button.hidden = !visible;
        if (visible) visibleCount++;
      }

      group.hidden = visibleCount === 0;
    }
  }

  private markSelection(): void {
    for (const [id, button] of this.buttons) {
      button.classList.toggle('selected', id === this.selectedId);
    }
  }

  private isPanelOpen(): boolean {
    return this.panelEl.classList.contains('open');
  }

  private setPanelOpen(open: boolean): void {
    this.panelEl.classList.toggle('open', open);
    this.toggleBtn.setAttribute('aria-expanded', String(open));
  }

  private renderGroups(): void {
    this.groupsEl.innerHTML = '';

    for (const [substanceClass, members] of substancesByClass()) {
      const group = document.createElement('section');
      group.className = 'substance-group';

      const heading = document.createElement('h3');
      heading.textContent = SUBSTANCE_CLASS_LABELS[substanceClass as SubstanceClass];
      group.appendChild(heading);

      const row = document.createElement('div');
      row.className = 'substance-row';

      for (const substance of members) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'substance-chip';
        button.textContent = `${substance.emoji} ${substance.displayName}`;
        // Aliases go into the search haystack so "coke" or "ssri" find their entry.
        button.dataset.search = [substance.displayName, ...substance.aliases]
          .join(' ')
          .toLowerCase();
        button.addEventListener('click', () => this.select(substance));
        row.appendChild(button);
        this.buttons.set(substance.id, button);
      }

      group.appendChild(row);
      this.groupsEl.appendChild(group);
    }
  }
}
